import React, { useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { lookupGstin, getGstinCaptcha, verifyGstinCaptcha } from "../pages/generic/crud";
import { GST_STATES } from "../utils/GstStates";

export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

const stateName = (gstin) => {
    const match = GST_STATES.find((s) => s.code === gstin.substring(0, 2));
    return match ? match.name : null;
};

/**
 * "Quick Fill with GST" hero bar for Customer/Supplier dialogs.
 * Type a GSTIN -> we fetch the public GST record and hand the parent a details
 * object to fill the form with. Falls back to the official GST portal with an
 * inline captcha when the free sources are unavailable.
 *
 * Props: value/onChange bind the parent's gstNumber field; onApply(details)
 * fills the form; disabled greys out the whole bar.
 */
export function GstinSmartFill({ value, onChange, onApply, disabled }) {
    const [phase, setPhase] = useState("idle"); // idle | loading | success | error | captcha | verifying
    const [result, setResult] = useState(null);
    const [message, setMessage] = useState(null);
    const [captcha, setCaptcha] = useState(null); // {sessionId, captchaImage}
    const [captchaText, setCaptchaText] = useState("");

    const gstin = (value || "").toUpperCase().replace(/\s/g, "");
    const valid = GSTIN_REGEX.test(gstin);
    const busy = phase === "loading" || phase === "verifying";

    const succeed = (details) => {
        setResult(details);
        setPhase("success");
        setCaptcha(null);
        setCaptchaText("");
        if (onApply) onApply(details);
    };

    const handleFetch = async () => {
        setPhase("loading");
        setMessage(null);
        setResult(null);
        try {
            const { data } = await lookupGstin(gstin);
            if (data?.found) {
                succeed(data.details);
            } else if (data?.captchaRequired) {
                await loadCaptcha("Free GST sources are busy right now — one quick captcha on the official portal and we'll fetch it from there.");
            } else {
                setMessage(data?.error || "No GST record found for this number.");
                setPhase("error");
            }
        } catch (error) {
            setMessage(error?.response?.data?.error || "Could not look up this GST number.");
            setPhase("error");
        }
    };

    const loadCaptcha = async (reason) => {
        try {
            const { data } = await getGstinCaptcha();
            setCaptcha(data);
            setCaptchaText("");
            setMessage(reason || null);
            setPhase("captcha");
        } catch (error) {
            setMessage(error?.response?.data?.error || "The GST portal is not reachable right now. You can still fill the form manually.");
            setPhase("error");
        }
    };

    const handleVerify = async () => {
        setPhase("verifying");
        try {
            const { data } = await verifyGstinCaptcha({ sessionId: captcha.sessionId, gstin, captcha: captchaText.trim() });
            if (data?.found) {
                succeed(data.details);
            } else {
                await loadCaptcha(data?.error || "That didn't match — try the new code.");
            }
        } catch (error) {
            await loadCaptcha(error?.response?.data?.error || "Verification failed — try the new code.");
        }
    };

    const reset = () => {
        setPhase("idle");
        setResult(null);
        setMessage(null);
        setCaptcha(null);
    };

    const active = result && String(result.status).toUpperCase() === "ACTIVE";

    return (
        <div className="rounded p-4 mb-4 border border-primary border-dashed bg-light-primary">
            <div className="d-flex align-items-center mb-3">
                <span className="symbol symbol-35px me-3">
                    <span className="symbol-label bg-primary">
                        <i className="fa fa-wand-magic-sparkles text-white fs-5"></i>
                    </span>
                </span>
                <span>
                    <span className="d-block fw-bold text-gray-900">Quick Fill with GST <span className="badge badge-primary fs-9 ms-1">NEW</span></span>
                    <span className="d-block fs-8 text-gray-600">Type the GST number — name, address and PAN fill themselves</span>
                </span>
            </div>

            <div className="d-flex gap-2">
                <Form.Control
                    value={gstin}
                    disabled={disabled || busy}
                    maxLength={15}
                    placeholder="15-character GST number, e.g. 29ACNFA5044P1ZQ"
                    style={{ fontFamily: "monospace", letterSpacing: "1px", textTransform: "uppercase" }}
                    onChange={(e) => { onChange(e.target.value.toUpperCase().replace(/\s/g, "")); if (phase !== "idle") reset(); }}
                    onKeyDown={(e) => { if (e.key === "Enter" && valid && !busy) { e.preventDefault(); handleFetch(); } }}
                />
                <Button variant="primary" className="text-nowrap" disabled={!valid || busy || disabled} onClick={handleFetch}>
                    {phase === "loading"
                        ? <><Spinner size="sm" animation="border" className="me-2" />Fetching…</>
                        : <><i className="fa fa-bolt me-2"></i>Auto-Fill</>}
                </Button>
            </div>

            {gstin && !valid && (
                <div className="fs-8 text-danger mt-2">
                    <i className="fa fa-circle-exclamation me-1"></i>
                    {15 - gstin.length > 0 ? `${15 - gstin.length} more character${15 - gstin.length === 1 ? "" : "s"} — ` : ""}
                    format: 2-digit state + 10-character PAN + 3 more (e.g. 29ACNFA5044P1ZQ)
                </div>
            )}
            {gstin && valid && phase === "idle" && (
                <div className="fs-8 text-success mt-2">
                    <i className="fa fa-circle-check me-1"></i>
                    Looks valid{stateName(gstin) ? ` — registered in ${stateName(gstin)}` : ""}. Press Auto-Fill.
                </div>
            )}

            {phase === "error" && (
                <div className="fs-8 text-warning mt-2 fw-semibold">
                    <i className="fa fa-triangle-exclamation me-1"></i>{message} You can still fill the form manually.
                </div>
            )}

            {phase === "success" && result && (
                <div className="bg-white rounded p-3 mt-3 border border-success border-dashed">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        <i className="fa fa-circle-check text-success fs-4"></i>
                        <span className="fw-bold text-gray-900">{result.legalName || result.tradeName}</span>
                        <span className={`badge badge-light-${active ? "success" : "danger"} fs-8`}>{result.status || "Unknown"}</span>
                        {result.constitution && <span className="badge badge-light fs-8 text-muted">{result.constitution}</span>}
                    </div>
                    {result.address && <div className="fs-8 text-gray-700 mt-1">{result.address}{result.pincode ? ` — ${result.pincode}` : ""}</div>}
                    <div className="fs-9 text-muted mt-1">
                        Fetched from public GST records ({result.source}). The form below is filled — please review before saving.
                        {!active && <span className="text-danger fw-semibold"> Note: this GSTIN is not Active.</span>}
                    </div>
                </div>
            )}

            {(phase === "captcha" || phase === "verifying") && captcha && (
                <div className="bg-white rounded p-3 mt-3 border border-warning border-dashed">
                    <div className="fw-semibold text-gray-900 fs-7 mb-1">
                        <i className="fa fa-shield-halved text-warning me-2"></i>One-time check from the official GST portal
                    </div>
                    {message && <div className="fs-8 text-gray-700 mb-2">{message}</div>}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <img src={captcha.captchaImage} alt="GST portal captcha" height={42} style={{ borderRadius: 6, border: "1px solid #e3e8ee" }} />
                        <Button variant="light" size="sm" title="New code" disabled={busy} onClick={() => loadCaptcha()}>
                            <i className="fa fa-rotate"></i>
                        </Button>
                        <Form.Control
                            value={captchaText}
                            disabled={busy}
                            maxLength={10}
                            placeholder="Type the code"
                            style={{ maxWidth: 150, fontFamily: "monospace" }}
                            onChange={(e) => setCaptchaText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && captchaText.trim() && !busy) { e.preventDefault(); handleVerify(); } }}
                        />
                        <Button variant="warning" size="sm" disabled={!captchaText.trim() || busy} onClick={handleVerify}>
                            {phase === "verifying"
                                ? <><Spinner size="sm" animation="border" className="me-2" />Checking…</>
                                : <>Verify &amp; Fill</>}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
